import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { UserPlus, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { LangSelect } from "@/components/LangSelect";
import { inviteLink, langName, MAX_PARTICIPANTS } from "@/lib/langs";

const shareInvite = async (url, lang, roomId) => {
  const text = `Join me on SpeechFlow (${roomId}) — you'll read and hear everything in ${langName(lang)}:`;
  if (navigator.share) {
    try {
      await navigator.share({ title: "SpeechFlow", text, url });
      return;
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, "_blank", "noopener");
};

export const InviteQrDialog = ({ room }) => {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const url = inviteLink(room.room_id, lang);
  const full = room.participants.length >= MAX_PARTICIPANTS;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost !h-9 !px-3 text-xs" data-testid="room-invite-button" title="Invite someone" aria-label="Invite someone"><UserPlus size={14} /><span className="hidden sm:inline">Invite</span></button>
      </DialogTrigger>
      <DialogContent className="max-w-sm rounded-none" data-testid="room-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Invite to SpeechFlow</DialogTitle>
          <DialogDescription>Choose the guest&apos;s language: they will read and hear everyone in it.</DialogDescription>
        </DialogHeader>
        {full ? (
          <p className="border border-[color:var(--au-red)] p-3 font-mono text-xs" data-testid="room-invite-full">ROOM FULL · {MAX_PARTICIPANTS}/{MAX_PARTICIPANTS} PEOPLE</p>
        ) : (
          <>
            <label className="block"><span className="mono-label">Guest language</span><div className="mt-2"><LangSelect value={lang} onChange={setLang} allowAuto={false} testId="room-invite-lang" /></div></label>
            <div className="mx-auto bg-white p-4 shadow-[0_0_30px_rgba(61,219,232,0.35)]">
              <QRCodeSVG value={url} size={200} level="M" bgColor="#ffffff" fgColor="#0A1628" data-testid="room-qr-code" />
            </div>
            <div className="text-center font-mono text-[11px] tracking-widest text-muted-foreground">ROOM {room.room_id} · {room.participants.length}/{MAX_PARTICIPANTS} PEOPLE</div>
            <div className="flex gap-2">
              <input readOnly value={url} className="field !h-10 font-mono text-xs" data-testid="room-qr-link" onFocus={(e) => e.target.select()} />
              <button onClick={() => { navigator.clipboard?.writeText(url); toast.success("Invite link copied"); }} className="icon-btn !h-10 !w-10 shrink-0" data-testid="room-copy-invite-button" aria-label="Copy link"><Copy size={15} /></button>
              <button onClick={() => shareInvite(url, lang, room.room_id)} className="icon-btn !h-10 !w-10 shrink-0" data-testid="room-share-invite-button" aria-label="Share invite"><Share2 size={15} /></button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
